import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GCS_BUCKET_URL } from '../../config/api';
import { checkConditionals } from '../../utils/helper';
import { getAllEndingsFromNovel } from '../../utils/endingsHelper';
import { getAllScenes } from '../../utils/novelUtils';
import { loadTestState, saveTestState, clearTestState, mergeSavedFlags, mergeSavedScales } from '../../utils/testStateStorage';
import './StaticScenePreview.css';

const StaticScenePreview = ({
  novelId,
  scene,
  novel,
  loading,
  isProduction = false
}) => {
  const navigate = useNavigate();
  const [musicPlaying, setMusicPlaying] = useState(true);
  const audioRef = useRef(null);
  const msgUiRef = useRef(null);
  const [spritesTop, setSpritesTop] = useState(0);

  // Routing-preview state (current flags / scales / completed endings)
  const [flags, setFlags] = useState([]);
  const [scales, setScales] = useState([]);
  const [completedEndingIds, setCompletedEndingIds] = useState([]);
  const [controlsExpanded, setControlsExpanded] = useState(false);
  const [endingsSearch, setEndingsSearch] = useState('');

  const allEndings = getAllEndingsFromNovel(novel);
  const allScenes = getAllScenes(novel);

  // Initialize flags/scales/completed endings, restoring any saved per-novel playthrough state.
  // Only run once the novel is loaded — otherwise flags/scales resolve to [] (no definitions yet)
  // and a save would clobber the stored state with empties.
  useEffect(() => {
    if (!novel) return;
    const saved = loadTestState(novelId);
    setFlags(mergeSavedFlags(novel?.states?.flags, saved?.flags));
    setScales(mergeSavedScales(novel?.states?.scales, saved?.scales));
    setCompletedEndingIds(saved?.completedEndingIds || []);
  }, [novel, novelId]);

  // Persist explicitly on user edits (below) rather than via a state-reading effect,
  // so storage is never written from the uninitialized/loading state.
  const persist = (nextFlags, nextScales, nextCompleted) => {
    if (!novel) return;
    const saved = loadTestState(novelId);
    saveTestState(novelId, {
      flags: nextFlags,
      scales: nextScales,
      completedEndingIds: nextCompleted,
      // preserve name/biography written by the interactive test canvas
      userCharacterName: saved?.userCharacterName,
      userCharacterBiography: saved?.userCharacterBiography
    });
  };

  // Calculate sprite top position based on message UI height
  useEffect(() => {
    const calcTop = () => {
      if (msgUiRef.current) {
        setSpritesTop(msgUiRef.current.offsetTop + msgUiRef.current.offsetHeight + 4);
      }
    };
    calcTop();
    const observer = new ResizeObserver(calcTop);
    if (msgUiRef.current) observer.observe(msgUiRef.current);
    window.addEventListener('resize', calcTop);
    return () => { observer.disconnect(); window.removeEventListener('resize', calcTop); };
  }, [scene?.original_message]);

  // Get background asset
  const getBackgroundAsset = () => {
    if (!scene?.background_id) return null;
    return novel?.assets?.backgrounds?.find(bg => bg.id === scene.background_id);
  };

  // Get character assets
  const getCharacterAssets = () => {
    if (!scene?.characters || scene.characters.length === 0) return [];

    return scene.characters.map(sceneChar => {
      const character = novel?.characters?.find(char => char.id === sceneChar.character_id);
      if (!character) return null;

      const spriteAsset = novel?.assets?.sprites?.find(sprite => sprite.id === sceneChar.sprite_id);

      return {
        character,
        spriteAsset,
        sceneCharacter: sceneChar
      };
    }).filter(Boolean);
  };

  // Get music asset
  const getMusicAsset = () => {
    if (!scene?.music_id) return null;
    return novel?.assets?.musics?.find(music => music.id === scene.music_id);
  };

  // Get speaker name
  const getSpeakerName = () => {
    if (scene?.speaker_type === 'user') {
      return 'User';
    }

    if (scene?.speaker_id) {
      const character = novel?.characters?.find(char => char.id === scene.speaker_id);
      if (character) {
        const nameTranslation = character.name_translations?.find(t => t.language === 'russian');
        if (nameTranslation?.translation) {
          return nameTranslation.translation;
        }
        return character.prompt_name || 'Неизвестный персонаж';
      }
    }

    return 'No speaker selected';
  };

  // Determine the next scene: highest-priority option whose conditionals pass
  // (mirrors backend static-scene routing in game_state.py)
  const getSelectedNextScene = () => {
    const opts = scene?.next_scenes || [];
    const cf = flags.map(f => ({ id: f.id, value: f.value }));
    const cs = scales.map(s => ({ id: s.id, value: s.value }));
    let selected = null;
    let highest = -Infinity;
    for (const opt of opts) {
      if (!opt.scene_id) continue;
      if (checkConditionals(opt.conditionals || [], cf, cs, completedEndingIds)) {
        if (opt.priority > highest) {
          highest = opt.priority;
          selected = opt;
        }
      }
    }
    return selected;
  };

  const getSceneName = (sid, sceneType) =>
    allScenes.find(s => s.id === sid && s.type === sceneType)?.name || 'Неизвестная сцена';

  // Navigate to the resolved next scene, opening its test/preview tab
  const goToNextScene = (ns) => {
    if (!ns?.scene_id) return;
    const sceneTypeUrl = ns.scene_type === 'interactive' ? 'interactive_scene'
      : ns.scene_type === 'open_world' ? 'open_world_scene' : 'static_scene';
    const tab = ns.scene_type === 'static' ? 'preview' : 'testCanvas';
    const basePath = isProduction ? 'production_change' : 'building';
    navigate(`/${basePath}/${novelId}/${sceneTypeUrl}/${ns.scene_id}?tab=${tab}`);
  };

  const updateFlagValue = (flagId, value) => {
    const next = flags.map(f => f.id === flagId ? { ...f, value } : f);
    setFlags(next);
    persist(next, scales, completedEndingIds);
  };
  const updateScaleValue = (scaleId, value) => {
    const next = scales.map(s => s.id === scaleId ? { ...s, value: parseInt(value) || 0 } : s);
    setScales(next);
    persist(flags, next, completedEndingIds);
  };
  const addCompletedEnding = (endingId) => {
    const next = [...completedEndingIds, endingId];
    setCompletedEndingIds(next);
    persist(flags, scales, next);
  };
  const removeCompletedEnding = (endingId) => {
    const next = completedEndingIds.filter(eid => eid !== endingId);
    setCompletedEndingIds(next);
    persist(flags, scales, next);
  };
  const resetState = () => {
    if (!window.confirm('Сбросить все флаги, шкалы и завершённые концовки к начальным значениям?')) return;
    clearTestState(novelId);
    setFlags((novel?.states?.flags || []).map(f => ({ id: f.id, name: f.name, value: f.initial_value })));
    setScales((novel?.states?.scales || []).map(s => ({ id: s.id, name: s.name, value: s.initial_value })));
    setCompletedEndingIds([]);
  };

  // Toggle music
  const toggleMusic = () => {
    setMusicPlaying(!musicPlaying);
    if (audioRef.current) {
      if (musicPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleAudioRef = (audio) => {
    audioRef.current = audio;
  };

  const backgroundAsset = getBackgroundAsset();
  const characterAssets = getCharacterAssets();
  const musicAsset = getMusicAsset();
  const prefix = isProduction ? 'production' : 'building';
  const selectedNextScene = getSelectedNextScene();

  return (
    <div className="sspv-wrap">
      <div className="sspv-header">
        <h3>{'Предварительный просмотр сцены'}</h3>
      </div>

      {/* Routing controls */}
      <div className="sspv-controls">
        <div className="sspv-controls-head">
          <h3 className="sspv-controls-title">Состояние для маршрутизации</h3>
          <button className="sspv-toggle-btn" onClick={() => setControlsExpanded(!controlsExpanded)}>
            {controlsExpanded ? '▾ Скрыть' : '▸ Открыть'}
          </button>
        </div>

        {controlsExpanded && (
          <div className="sspv-controls-body">
            {flags.length > 0 && (
              <div className="sspv-section">
                <h4>Флаги</h4>
                <div className="sspv-flags">
                  {flags.map((flag) => (
                    <div key={flag.id} className="sspv-flag-item">
                      <label>{flag.name}:
                        <input
                          type="checkbox"
                          checked={flag.value}
                          onChange={(e) => updateFlagValue(flag.id, e.target.checked)}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {scales.length > 0 && (
              <div className="sspv-section">
                <h4>Шкалы</h4>
                <div className="sspv-scales">
                  {scales.map((scale) => (
                    <div key={scale.id} className="sspv-scale-item">
                      <label>{scale.name}:
                        <input
                          type="number"
                          value={scale.value}
                          onChange={(e) => updateScaleValue(scale.id, e.target.value)}
                          className="sspv-scale-input"
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="sspv-section">
              <h4>Завершенные концовки</h4>
              <div className="sspv-endings-section">
                {allEndings && allEndings.length > 0 ? (
                  <>
                    {completedEndingIds.length > 0 && (
                      <div className="sspv-completed-tags">
                        {completedEndingIds.map(id => {
                          const ending = allEndings.find(e => e.id === id);
                          return ending ? (
                            <div key={id} className="sspv-completed-tag">
                              <span>{ending.name}</span>
                              <button
                                className="sspv-completed-tag-remove"
                                onClick={() => removeCompletedEnding(id)}
                              >×</button>
                            </div>
                          ) : null;
                        })}
                      </div>
                    )}
                    <input
                      type="text"
                      className="sspv-endings-search"
                      placeholder="Поиск концовки для добавления..."
                      value={endingsSearch}
                      onChange={(e) => setEndingsSearch(e.target.value)}
                    />
                    {endingsSearch.trim() && (() => {
                      const opts = allEndings.filter(e =>
                        e.name.toLowerCase().includes(endingsSearch.toLowerCase()) &&
                        !completedEndingIds.includes(e.id)
                      );
                      return (
                        <div className="sspv-endings-dropdown">
                          {opts.length > 0 ? opts.map(ending => (
                            <div
                              key={ending.id}
                              className="sspv-endings-option"
                              onClick={() => {
                                addCompletedEnding(ending.id);
                                setEndingsSearch('');
                              }}
                            >
                              {ending.name}
                            </div>
                          )) : (
                            <div className="sspv-endings-no-results">Концовок не найдено</div>
                          )}
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <div className="sspv-no-endings">В этом романе не определены концовки</div>
                )}
              </div>
            </div>

            <div className="sspv-controls-actions">
              <button className="sspv-reset-btn" onClick={resetState}>
                Сбросить к начальным значениям
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Next-scene routing banner */}
      <div className="sspv-ending-banner">
        {selectedNextScene ? (
          <>
            <h3>Следующая сцена: {getSceneName(selectedNextScene.scene_id, selectedNextScene.scene_type)}</h3>
            <button className="sspv-next-scene-btn" onClick={() => goToNextScene(selectedNextScene)}>
              Перейти к следующей сцене →
            </button>
          </>
        ) : (
          <h3>Конец истории — нет подходящей следующей сцены</h3>
        )}
      </div>

      <div className="sspv-canvas-wrap">
        <div className="sspv-scene">
          {/* Background */}
          <div className={`sspv-bg${loading ? ' sspv-bg--loading' : ''}`}>
            {backgroundAsset ? (
              <img
                src={`${GCS_BUCKET_URL}/${prefix}/${novelId}/background/${backgroundAsset.id}.${backgroundAsset.extension}${backgroundAsset.cache_id ? `?cache_id=${backgroundAsset.cache_id}` : ''}`}
                alt=""
                className="sspv-bg-img"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="sspv-no-bg">
                <p>{'Фон не выбран'}</p>
              </div>
            )}
          </div>

          {/* Message UI at top */}
          <div className="sspv-msg-ui" ref={msgUiRef}>
            <div className="sspv-msg-box">
              {/* Toolbar */}
              {musicAsset && (
                <div className="sspv-toolbar">
                  <button
                    className="sspv-music-btn"
                    onClick={toggleMusic}
                    title={musicPlaying ? 'Пауза' : 'Воспроизвести'}
                  >
                    {musicPlaying ? '🔊' : '🔇'}
                  </button>
                </div>
              )}

              {/* Message */}
              {scene?.original_message && (
                <div className="sspv-char-msg">
                  <span className="sspv-char-name">{getSpeakerName()}:</span>
                  <span className="sspv-char-text">{scene.original_message}</span>
                </div>
              )}
            </div>
          </div>

          {/* Character Sprites */}
          <div className="sspv-sprites" style={{ top: spritesTop }}>
            {characterAssets.length > 0 ? (
              characterAssets.map((charAsset, index) => {
                if (!charAsset.spriteAsset) return null;

                const totalSprites = characterAssets.filter(ca => ca.spriteAsset).length;
                const spriteWidth = 90 / totalSprites;
                const totalWidthUsed = totalSprites * spriteWidth;
                const startPosition = (100 - totalWidthUsed) / 2;
                const leftPosition = startPosition + (index * spriteWidth);

                return (
                  <div
                    key={charAsset.character.id}
                    className="sspv-sprite"
                    style={{
                      left: `${leftPosition}%`,
                      width: `${spriteWidth}%`,
                      height: '100%',
                      zIndex: 10 + totalSprites - index
                    }}
                  >
                    <img
                      src={`${GCS_BUCKET_URL}/${prefix}/${novelId}/sprite/${charAsset.spriteAsset.id}.${charAsset.spriteAsset.extension}${charAsset.spriteAsset.cache_id ? `?cache_id=${charAsset.spriteAsset.cache_id}` : ''}`}
                      alt=""
                      className="sspv-sprite-img"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                );
              })
            ) : (
              <div className="sspv-no-chars">
                <p>{'Персонажи не настроены'}</p>
              </div>
            )}
          </div>

          {/* Hidden Audio Player */}
          {musicAsset && (
            <audio
              ref={handleAudioRef}
              autoPlay={musicPlaying}
              loop
              style={{ display: 'none' }}
            >
              <source
                src={`${GCS_BUCKET_URL}/${prefix}/${novelId}/music/${musicAsset.id}.${musicAsset.extension}${musicAsset.cache_id ? `?cache_id=${musicAsset.cache_id}` : ''}`}
                type={`audio/${musicAsset.extension}`}
              />
            </audio>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaticScenePreview;
